import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'

import { useModal } from '@/hooks/useModal'
import { useToast } from '@/hooks/useToast'
import {
  createCampaign,
  fetchCampaigns,
  updateCampaign,
  type CreateCampaignPayload,
} from '@/services/campaigns'
import { handleSetCampaigns } from '@/storage/modules/campaigns/reducer'
import { CampaignDataProps } from '@/types/campaignProps'

import { Button } from '../Button'

interface FormCampaignProps {
  campaign?: CampaignDataProps
}

interface CampaignFormValues {
  type: CampaignDataProps['type']
  title: string
  body: string
  imageUrl: string
  ctaType: CampaignDataProps['ctaType']
  ctaValue: string
  startsAt: string
  expiresAt: string
}

const campaignTypeOptions = [
  { label: 'Generic', value: 'generic' },
  { label: 'Album', value: 'album' },
  { label: 'Artist', value: 'artist' },
  { label: 'Version', value: 'version' },
]

const ctaTypeOptions = [
  { label: 'None', value: 'NONE' },
  { label: 'Open album', value: 'OPEN_ALBUM' },
  { label: 'Open artist', value: 'OPEN_ARTIST' },
  { label: 'Open external URL', value: 'OPEN_EXTERNAL_URL' },
]

function toIsoDateTime(value: string) {
  if (!value) return undefined

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return undefined
  }

  return date.toISOString()
}

function toDateTimeLocal(value?: string | null) {
  if (!value) return ''

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return ''
  }

  const offset = date.getTimezoneOffset()
  const localDate = new Date(date.getTime() - offset * 60000)

  return localDate.toISOString().slice(0, 16)
}

function requiresCtaValue(ctaType: CampaignFormValues['ctaType']) {
  return ctaType !== 'NONE'
}

export function FormCampaign({ campaign }: FormCampaignProps) {
  const dispatch = useDispatch()
  const { closeModal } = useModal()
  const { showToast } = useToast()
  const [isLoading, setIsLoading] = useState(false)

  const { register, handleSubmit, setValue, watch } =
    useForm<CampaignFormValues>({
      defaultValues: {
        type: 'generic',
        title: '',
        body: '',
        imageUrl: '',
        ctaType: 'NONE',
        ctaValue: '',
        startsAt: '',
        expiresAt: '',
      },
    })

  const selectedCtaType = watch('ctaType')

  const refreshCampaigns = async () => {
    const campaigns = await fetchCampaigns()
    dispatch(handleSetCampaigns({ campaigns }))
  }

  const onSubmit = async (data: CampaignFormValues) => {
    if (requiresCtaValue(data.ctaType) && !data.ctaValue.trim()) {
      showToast('CTA value is required for selected CTA type', {
        type: 'warning',
        theme: 'light',
      })
      return
    }

    const payload: CreateCampaignPayload = {
      type: data.type,
      title: data.title.trim(),
      body: data.body.trim(),
      ctaType: data.ctaType,
    }

    const imageUrl = data.imageUrl.trim()
    if (imageUrl) {
      payload.imageUrl = imageUrl
    }

    const ctaValue = data.ctaValue.trim()
    if (ctaValue) {
      payload.ctaValue = ctaValue
    }

    const startsAt = toIsoDateTime(data.startsAt)
    if (startsAt) {
      payload.startsAt = startsAt
    }

    const expiresAt = toIsoDateTime(data.expiresAt)
    if (expiresAt) {
      payload.expiresAt = expiresAt
    }

    try {
      setIsLoading(true)

      if (campaign) {
        await updateCampaign(campaign.id, payload)
        showToast('Campaign updated successfully', {
          type: 'success',
          theme: 'light',
        })
      } else {
        await createCampaign(payload)
        showToast('Campaign draft created successfully', {
          type: 'success',
          theme: 'light',
        })
      }

      await refreshCampaigns()
      closeModal()
    } catch (err: unknown) {
      const apiMessage =
        typeof err === 'object' &&
        err !== null &&
        'response' in err &&
        typeof err.response === 'object' &&
        err.response !== null &&
        'data' in err.response &&
        typeof err.response.data === 'object' &&
        err.response.data !== null &&
        'message' in err.response.data &&
        typeof err.response.data.message === 'string'
          ? err.response.data.message
          : 'Error saving campaign draft'

      showToast(apiMessage, {
        type: 'error',
        theme: 'light',
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!campaign) return

    setValue('type', campaign.type)
    setValue('title', campaign.title)
    setValue('body', campaign.body)
    setValue('imageUrl', campaign.imageUrl ?? '')
    setValue('ctaType', campaign.ctaType)
    setValue('ctaValue', campaign.ctaValue ?? '')
    setValue('startsAt', toDateTimeLocal(campaign.startsAt))
    setValue('expiresAt', toDateTimeLocal(campaign.expiresAt))
  }, [campaign, setValue])

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <div>
        <h4 className="mb-3 text-sm font-bold">Campaign type</h4>
        <select
          {...register('type')}
          className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
        >
          {campaignTypeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <h4 className="mb-3 text-sm font-bold">Title</h4>
        <input
          {...register('title', { required: true })}
          placeholder="Campaign title"
          className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
        />
      </div>

      <div>
        <h4 className="mb-3 text-sm font-bold">Message</h4>
        <textarea
          {...register('body', { required: true })}
          placeholder="Campaign message"
          rows={4}
          className="w-full resize-none rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
        />
      </div>

      <div>
        <h4 className="mb-3 text-sm font-bold">Image URL (optional)</h4>
        <input
          {...register('imageUrl')}
          placeholder="https://..."
          className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
        />
      </div>

      <div>
        <h4 className="mb-3 text-sm font-bold">CTA type</h4>
        <select
          {...register('ctaType')}
          className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
        >
          {ctaTypeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <h4 className="mb-3 text-sm font-bold">
          CTA value {selectedCtaType !== 'NONE' ? '(required)' : '(optional)'}
        </h4>
        <input
          {...register('ctaValue')}
          placeholder="albumId, artistId or external URL"
          className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
        />
      </div>

      <div className="grid grid-cols-1 gap-3">
        <div>
          <h4 className="mb-3 text-sm font-bold">Starts at (optional)</h4>
          <input
            {...register('startsAt')}
            type="datetime-local"
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
          />
        </div>

        <div>
          <h4 className="mb-3 text-sm font-bold">Expires at (optional)</h4>
          <input
            {...register('expiresAt')}
            type="datetime-local"
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
          />
        </div>
      </div>

      <Button
        title={campaign ? 'Update draft' : 'Create draft'}
        variant="purple"
        isLoading={isLoading}
      />
    </form>
  )
}
